/**
 * useEditableHighlightField — resolve a highlight card's property/association to
 * the matching editable field descriptor, or null when the field isn't editable.
 *
 * Reuses useUpdatableFields (the same eligibility the bulk-update / inline edit
 * flows use), so a card only shows its Edit pencil when the field can actually
 * be edited from this surface — and, like the overview section's inline rows,
 * only when the current principal holds the matching write grant. Schema
 * write-access says the field *can* be written; the permission says *this
 * user* may — a read-only seat gets neither pencil nor modal.
 */

import { useMemo } from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import type { UpdatableField } from '@/lib/crm/hooks/useUpdatableFields';
import { useUpdatableFields } from '@/lib/crm/hooks/useUpdatableFields';

export function useEditableHighlightField(
  orgId: number,
  entityTypeName: string,
  kind: UpdatableField['kind'],
  name: string
): UpdatableField | null {
  const { hasPermission } = usePermissions();
  // Mirrors overview-section.tsx: properties need ahoy_property_updated;
  // associations need both the add and the remove grant (a set edit does both).
  const canWriteKind =
    kind === 'property'
      ? hasPermission(entityTypeName, 'ahoy_property_updated')
      : hasPermission(entityTypeName, 'ahoy_association_added') &&
        hasPermission(entityTypeName, 'ahoy_association_removed');

  // Inline single-entity edit supports many_to_many associations (set add/remove
  // diff via useFormState), so opt them in here — unlike the bulk-update modal.
  const { fields } = useUpdatableFields(orgId, entityTypeName, {
    includeMultiValueAssociations: true,
  });
  return useMemo(
    () =>
      canWriteKind
        ? (fields.find(f => f.kind === kind && f.name === name) ?? null)
        : null,
    [canWriteKind, fields, kind, name]
  );
}
